import React, { createContext, useContext, useState } from "react";
import * as categoryService from "../services/categoryService";
import { useNotification } from "./NotificationContext";
import axios from "axios";

export interface Category {
  slug: string;
  courseCount: number;
  title: { en: string; ar: string };
  description: { en: string; ar: string };
  image: string;
  createdAt: string;
  updatedAt: string;
}

interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface CategoryContextType {
  categories: Category[];
  meta: PaginationMeta | null;
  loading: boolean;
  error: string | null;
  fetchCategories: (params?: any) => Promise<void>;
  createCategory: (formData: FormData) => Promise<void>;
  updateCategory: (slug: string, formData: FormData) => Promise<void>;
  deleteCategory: (slug: string) => Promise<void>;
}

const CategoryContext = createContext<CategoryContextType | undefined>(
  undefined
);

export const CategoryProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showSuccess, showError } = useNotification();

  const handleApiError = (error: unknown) => {
    if (axios.isAxiosError(error)) {
      const errorMessage =
        error.response?.data?.message || error.message || "An error occurred";
      setError(errorMessage);
      showError(errorMessage);
    } else {
      const errorMessage = "An unexpected error occurred";
      setError(errorMessage);
      showError(errorMessage);
    }
  };

  const fetchCategories = async (params = { page: 1, limit: 10 }) => {
    setLoading(true);
    setError(null);
    try {
      const res = await categoryService.getPaginatedCategories(params);
      setCategories(res.data);
      setMeta(res.meta);
    } catch (e) {
      handleApiError(e);
    } finally {
      setLoading(false);
    }
  };

  const createCategory = async (formData: FormData) => {
    setLoading(true);
    setError(null);
    try {
      const category = await categoryService.createCategory(formData);
      setCategories((prev) => [category, ...prev]);
      showSuccess("Category created successfully");
    } catch (e) {
      handleApiError(e);
    } finally {
      setLoading(false);
    }
  };

  const updateCategory = async (slug: string, formData: FormData) => {
    setLoading(true);
    setError(null);
    try {
      const updated = await categoryService.updateCategory(slug, formData);
      setCategories((prev) => prev.map((c) => (c.slug === slug ? updated : c)));
      showSuccess("Category updated successfully");
    } catch (e) {
      handleApiError(e);
    } finally {
      setLoading(false);
    }
  };

  const deleteCategory = async (slug: string) => {
    setLoading(true);
    setError(null);
    try {
      await categoryService.deleteCategory(slug);
      setCategories((prev) => prev.filter((c) => c.slug !== slug));
      showSuccess("Category deleted successfully");
    } catch (e) {
      handleApiError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <CategoryContext.Provider
      value={{
        categories,
        meta,
        loading,
        error,
        fetchCategories,
        createCategory,
        updateCategory,
        deleteCategory,
      }}
    >
      {children}
    </CategoryContext.Provider>
  );
};

export const useCategory = () => {
  const context = useContext(CategoryContext);
  if (!context)
    throw new Error("useCategory must be used within a CategoryProvider");
  return context;
};
